import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { api, ApiError } from '../api';

type Obj = Record<string, unknown>;

interface AppRow {
  app_code: string;
  display_name: string;
  status: string;
}

// "Add app" — provisions a registered app for a user (docs/admin.md §6.2, §7.3). Always provisions
// a blank doc; per-user apps (LinkedIn) are edited via the raw-JSON editor on UserDetail.
// captomi_office is tenant-scoped now (issue #15) — provisioned implicitly, not offered here.
// The hand-coded typed-form path (AppDataForm + app_data_forms/) was retired with #15 WS3.
export default function AddAppPanel({
  tenant_id,
  user_id,
  existing_apps,
  on_added,
}: {
  tenant_id: string;
  user_id: string;
  existing_apps: string[];
  on_added: () => void;
}) {
  const [open, set_open] = useState(false);
  const [picked, set_picked] = useState('');
  const [busy, set_busy] = useState(false);
  const [error, set_error] = useState<string | null>(null);

  const appsQuery = useQuery({
    queryKey: ['apps'],
    queryFn: () => api.get<{ apps: AppRow[] }>('/api/apps'),
    enabled: open,
  });
  const available = (appsQuery.data?.apps ?? []).filter(
    // captomi_office is tenant-scoped (issue #15) — provisioned implicitly, edited on TenantDetail;
    // not offered for user-scoped provisioning here.
    (a) => a.status === 'active' && !existing_apps.includes(a.app_code) && a.app_code !== 'captomi_office',
  );

  async function provision(data?: Obj) {
    set_busy(true);
    set_error(null);
    try {
      await api.post(`/api/tenants/${tenant_id}/users/${user_id}/apps`, {
        app_code: picked,
        ...(data ? { data } : {}),
      });
      set_open(false);
      set_picked('');
      on_added();
    } catch (err) {
      set_error(err instanceof ApiError ? err.reason : String(err));
      throw err;
    } finally {
      set_busy(false);
    }
  }

  if (!open) {
    return (
      <button onClick={() => set_open(true)} style={{ marginBottom: 16 }}>
        Add app
      </button>
    );
  }

  return (
    <div className="card" style={{ marginBottom: 16 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 12 }}>
        <h3 style={{ margin: 0 }}>Add app</h3>
        <button
          className="secondary"
          style={{ marginLeft: 'auto', padding: '4px 10px', fontSize: 12 }}
          onClick={() => {
            set_open(false);
            set_picked('');
            set_error(null);
          }}
        >
          Cancel
        </button>
      </div>

      <label style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}>
        <span style={{ fontSize: 13, fontWeight: 600 }}>App</span>
        <select value={picked} onChange={(e) => set_picked(e.target.value)}>
          <option value="">— select an app —</option>
          {available.map((a) => (
            <option key={a.app_code} value={a.app_code}>
              {a.display_name} ({a.app_code})
            </option>
          ))}
        </select>
      </label>

      {appsQuery.data && available.length === 0 && (
        <p className="muted" style={{ fontSize: 13 }}>
          This user already has every active registered app.
        </p>
      )}
      {error && <div className="error" style={{ marginBottom: 12 }}>{error}</div>}

      {picked && (
        <div style={{ display: 'grid', gap: 8 }}>
          <p className="muted" style={{ fontSize: 13, margin: 0 }}>
            Provisions an empty document for <code>{picked}</code>; edit as raw JSON afterwards.
          </p>
          <div>
            <button onClick={() => void provision()} disabled={busy}>
              {busy ? 'Provisioning...' : 'Provision (blank)'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
